import { c as _c } from "react/compiler-runtime";
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import MetricTable from '../../../common/metrics/MetricTable';
import type { MetricTableProps, MetricTableRow } from '../../../common/metrics/MetricTable';
import { useOutpostMetricsQuery } from '../../../hooks/useOutpostMetricsQuery';
import type { OutpostMetricsEndpoint } from '../../../hooks/useOutpostMetricsQuery';
import type { DateRange } from '../../DashboardScene/Metrics/MetricDatePicker';
import { getOutpostTenantDisplayName, useOutpostTenantsById } from '../utils/tenantDisplay';
export interface MetricTableChartConfig {
  title: string;
  description?: string;
  endpoint: OutpostMetricsEndpoint;
  measures: string[];
  dimensions: string[];
  dimension_key?: string;
  filters?: Record<string, string | string[]>;
  date_range: DateRange;
  refresh_key: number;
  get_color?: (dimension: string) => string | undefined;
  show_error_rate?: boolean;
  max_h?: MetricTableProps['max_h'];
}
export const useMetricTableChartData = t0 => {
  const $ = _c(36);
  const {
    endpoint,
    measures,
    dimensions,
    dimension_key,
    filters,
    date_range,
    get_color,
    show_error_rate
  } = t0;
  const model = endpoint === "events" ? "Events" : "Attempts";
  let t1;
  if ($[0] !== date_range.max || $[1] !== date_range.min || $[2] !== dimensions || $[3] !== endpoint || $[4] !== filters || $[5] !== measures) {
    t1 = date_range.min && date_range.max ? {
      endpoint,
      time: {
        start: date_range.min,
        end: date_range.max
      },
      measures,
      dimensions,
      filters
    } : null;
    $[0] = date_range.max;
    $[1] = date_range.min;
    $[2] = dimensions;
    $[3] = endpoint;
    $[4] = filters;
    $[5] = measures;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const query = t1;
  const {
    raw_data,
    is_loading,
    error
  } = useOutpostMetricsQuery(query);
  const resolved_dimension_key = dimension_key || dimensions[0];
  const is_tenant_dimension = resolved_dimension_key === "tenant_id";
  let t2;
  if ($[7] !== is_tenant_dimension || $[8] !== model || $[9] !== raw_data || $[10] !== resolved_dimension_key) {
    t2 = is_tenant_dimension ? raw_data?.map(datum => datum[`${model}.${resolved_dimension_key}`]).filter(Boolean) || [] : [];
    $[7] = is_tenant_dimension;
    $[8] = model;
    $[9] = raw_data;
    $[10] = resolved_dimension_key;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const tenant_ids = t2;
  const {
    tenants_by_id
  } = useOutpostTenantsById(tenant_ids);
  let t3;
  bb0: {
    if (is_loading) {
      t3 = null;
      break bb0;
    }
    if (!raw_data || raw_data.length === 0) {
      let t4;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      t3 = t4;
      break bb0;
    }
    if (!resolved_dimension_key) {
      let t4;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[14] !== get_color || $[15] !== is_tenant_dimension || $[16] !== model || $[17] !== raw_data || $[18] !== resolved_dimension_key || $[19] !== show_error_rate || $[20] !== tenants_by_id) {
      let t5;
      if ($[22] !== get_color || $[23] !== is_tenant_dimension || $[24] !== model || $[25] !== resolved_dimension_key || $[26] !== show_error_rate || $[27] !== tenants_by_id) {
        t5 = datum_0 => {
          const dimension = String(datum_0[`${model}.${resolved_dimension_key}`] || "unknown");
          const count = parseFloat(datum_0[`${model}.count`]) || 0;
          const error_rate = datum_0[`${model}.error_rate`] != null ? parseFloat(datum_0[`${model}.error_rate`]) : undefined;
          return {
            key: dimension,
            label: is_tenant_dimension ? getOutpostTenantDisplayName(tenants_by_id[dimension]) || dimension : dimension,
            color: get_color?.(dimension),
            value: count,
            display_value: String(count),
            extra_value: show_error_rate && error_rate != null ? `${(error_rate * 100).toFixed(1)}%` : undefined,
            tooltip: [{
              label: "Count",
              value: String(count)
            }, ...(show_error_rate && error_rate != null ? [{
              label: "Error rate",
              value: `${(error_rate * 100).toFixed(1)}%`
            }] : [])]
          };
        };
        $[22] = get_color;
        $[23] = is_tenant_dimension;
        $[24] = model;
        $[25] = resolved_dimension_key;
        $[26] = show_error_rate;
        $[27] = tenants_by_id;
        $[28] = t5;
      } else {
        t5 = $[28];
      }
      t4 = raw_data.map(t5).sort(_temp);
      $[14] = get_color;
      $[15] = is_tenant_dimension;
      $[16] = model;
      $[17] = raw_data;
      $[18] = resolved_dimension_key;
      $[19] = show_error_rate;
      $[20] = tenants_by_id;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    t3 = t4;
  }
  const rows = t3;
  let t4;
  if ($[29] !== rows || $[30] !== show_error_rate) {
    t4 = show_error_rate && rows && rows.length > 0 ? <Div flex={{
      gap: 3
    }}><Text muted={true} size="xs">Count</Text><Text muted={true} size="xs" style={{
        minWidth: "48px",
        textAlign: "right"
      }}>Error %</Text></Div> : undefined;
    $[29] = rows;
    $[30] = show_error_rate;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  const header_right = t4;
  const t5 = !!error;
  let t6;
  if ($[32] !== header_right || $[33] !== rows || $[34] !== t5) {
    t6 = {
      rows,
      error: t5,
      header_right
    };
    $[32] = header_right;
    $[33] = rows;
    $[34] = t5;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  return t6;
};
const MetricTableChart: React.FC<MetricTableChartConfig> = t0 => {
  const $ = _c(12);
  let config;
  let description;
  let max_h;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      description,
      max_h,
      ...config
    } = t0);
    $[0] = t0;
    $[1] = config;
    $[2] = description;
    $[3] = max_h;
    $[4] = title;
  } else {
    config = $[1];
    description = $[2];
    max_h = $[3];
    title = $[4];
  }
  const data = useMetricTableChartData(config);
  let t1;
  if ($[5] !== data.error || $[6] !== data.header_right || $[7] !== data.rows || $[8] !== description || $[9] !== max_h || $[10] !== title) {
    t1 = <MetricTable title={title} description={description} rows={data.rows} error={data.error} header_right={data.header_right} max_h={max_h} />;
    $[5] = data.error;
    $[6] = data.header_right;
    $[7] = data.rows;
    $[8] = description;
    $[9] = max_h;
    $[10] = title;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
};
export default MetricTableChart;
function _temp(a, b) {
  return b.value - a.value;
}